import { Card, CardContent } from "../../ui/card"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { useMemo } from "react"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useEcommSales } from "../hooks/useSalesEcomm";
import { DataTable } from "../../ui/data-table/data-table"
import { formatCurrency } from "../../../utils/formatCurrency"
import { formatNumber } from "../../../utils/formatNumber"


interface CategoryPerformanceProps {
  selectedMonth: string;
}


interface CategoryPerformanceMetrics {
  category: string;
  revenue: number;
  averagePrice: number;
  units: number;
  averageOrderValue: number;
}

export function EcommCategoryPerformance({ selectedMonth }: CategoryPerformanceProps) {

  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");


  const { data, isLoading } = useEcommSales(startDate, endDate);

  const shopifyData: CategoryPerformanceMetrics[] = useMemo(() =>
    (data?.shopify_category_performance || []).map((cat) => ({
      category: cat.category,
      revenue: cat.revenue,
      averagePrice: cat.average_price,
      units: cat.units_sold,
      averageOrderValue: cat.average_order,
    }))
    , [data]);

  const noData = shopifyData.length === 0;

  const columns = [
    {
      accessorKey: "category",
      header: "Category"
    },
    {
      accessorKey: "averagePrice",
      header: "Avg. Price",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "units",
      header: "Units",
      isNumber: true,
      cell: (info: any) => formatNumber(info.getValue()),
    },
    {
      accessorKey: "averageOrderValue",
      header: "AOV",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "revenue",
      header: "Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },   
  ]

  const colors = useChartColors("ecommerce")
  if (!colors) return null;
  return (
    <>
      <SummaryHeader
        title="General"
      />
      <Card>
        <CardContent>
          <Tabs defaultValue="chart" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center justify-between mb-4">
              <TabsList className="flex flex-wrap gap-1">
                <TabsTrigger value="chart">Chart</TabsTrigger>
                <TabsTrigger value="pie">Distribution</TabsTrigger>
                <TabsTrigger value="table">Table</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="chart" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              {isLoading ? (
                <SalesLoader variant="performance-bar" />
              ) : noData ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No Data Available
                </div>) : (
                <><ResponsiveContainer width="100%" height={350}>
                  <BarChart data={shopifyData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value).replace(/[^0-9.KMB]/g, "")} />
                    <YAxis type="category" dataKey="category" width={110} interval={0} />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: "0.875rem",
                      }}
                      itemStyle={{
                        color: "var(--foreground)",
                      }} />

                    <Bar dataKey="revenue" name="Revenue">
                      {shopifyData.map((_entry, index) => (
                        <Cell key={`bar-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer><div className="flex justify-center gap-4 mt-4 px-2 flex-wrap">
                    {shopifyData.map((entry, index) => (
                      <div key={entry.category} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-sm inline-block"
                          style={{ backgroundColor: colors[index % colors.length] }} />
                        <span>{entry.category}</span>
                      </div>
                    ))}
                  </div></>
              )}
            </TabsContent>
            <TabsContent value="pie" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              {isLoading ? (
                <SalesLoader variant="performance-pie" />
              ) : noData ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No Data Available
                </div>) : (
                <><ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={shopifyData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill={colors[0]}
                      dataKey="revenue"
                      nameKey="category"
                    >
                      {shopifyData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: "0.875rem",
                      }}
                      itemStyle={{
                        color: "var(--foreground)",
                      }} />
                  </PieChart>
                </ResponsiveContainer>
                  <div className="mt-4 flex flex-wrap justify-center gap-4">
                    {shopifyData.map((entry, index) => {
                      const total = shopifyData.reduce((sum, d) => sum + d.revenue, 0)
                      const percent = total ? (entry.revenue / total) * 100 : 0
                      return (
                        <div key={entry.category} className="flex items-center gap-2 text-sm">
                          <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: colors[index % colors.length] }} />
                          <span>{entry.category}: {percent.toFixed(2)}%</span>
                        </div>
                      )
                    })}
                  </div></>
              )}
            </TabsContent>
            <TabsContent value="table" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              <div className="rounded-md border max-h-[550px] overflow-auto p-4">
                {isLoading ? (
                  <SalesLoader variant="performance-channel" />
                ) : noData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={shopifyData}
                    searchColumn="category"
                    searchPlaceholder="Search by Category..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["revenue"]} // Columns to total
                  />
                )}      
                </div>  
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  )
}
